import {
	useEffect,
	useState,
	useImperativeHandle,
	type ForwardedRef,
	type ReactNode,
} from 'react';

import type { StrategySeed } from './DockMenu';
import {
	fieldForStep,
	groundingTally,
	type GroundingTally,
	isNorthStarStep,
} from './groundingFields';
import { GroundingReviewStep } from './GroundingReviewStep';
import { GroundingStepCard } from './GroundingStepCard';
import { formatNorthStar } from './northStar';
import { REVIEW_STEP, useGroundingFlow } from './useGroundingFlow';

// The guided grounding capture: one question per card inside the dock's create view, with a
// vertical shuttle between them. Both cards are mounted during a transition — the outgoing one
// is held just long enough to play its exit — so the stage is absolutely positioned and the
// leaving card is taken out of the a11y tree and out of hit-testing the moment it starts to go.

export interface GroundingFlowHandle {
	/** Persist whatever is pending; resolves to the saved markdown, or null on failure. */
	flush: () => Promise<string | null>;
}

/** What the panel's own action row needs, so it can live outside the conversation card. */
export interface GroundingProgress {
	/** 1-based question number, or null on the summary. */
	step: number | null;
	total: number;
	/**
	 * Facts we still need, counting only what the OWNER has answered. It used to count any
	 * FILLED field, which reads zero from question one — every field arrives pre-filled with our
	 * guesses, so the row claimed "Nothing left to check" while still asking nine questions.
	 */
	remaining: number;
	/** True while the owner is typing their own answer, when the hint no longer applies. */
	writingOwn: boolean;
	/** False on the first question, which has no earlier step to return to. */
	canGoBack: boolean;
	onBack: () => void;
}

export function GroundingFlow({
	businessId,
	label,
	strategySeed,
	footerAction,
	onProgress,
	ref,
}: {
	businessId: string;
	label: string;
	/** Pre-filled answers from a propose_new_strategy recommendation. */
	strategySeed?: StrategySeed;
	/** Reports the row's state upward; the create view renders it below the card. */
	onProgress?: (p: GroundingProgress) => void;
	/** The Generate button. Owned by StrategyCreateView; this only positions it. */
	footerAction?: ReactNode;
	ref?: ForwardedRef<GroundingFlowHandle>;
}) {
	const flow = useGroundingFlow(businessId, label, strategySeed);
	const [writingOwn, setWritingOwn] = useState(false);
	useImperativeHandle(ref, () => ({ flush: flow.flush }), [flow.flush]);

	// Counted ONCE, here, and handed to everything that reports a number: the row below the card,
	// the meter on this element, and the summary. See GroundingTally for why `filled` and
	// `checked` are different figures.
	const tally = groundingTally(
		flow.answers,
		Boolean(flow.northStar),
		flow.answeredByOwner,
	);

	useEffect(() => {
		onProgress?.({
			step: flow.stepIndex === REVIEW_STEP ? null : flow.stepIndex + 1,
			total: tally.total,
			remaining: tally.remaining,
			writingOwn,
			canGoBack: flow.stepIndex > 0,
			onBack: flow.cancelStep,
		});
	}, [
		onProgress,
		flow.stepIndex,
		flow.cancelStep,
		writingOwn,
		tally.total,
		tally.remaining,
	]);

	if (flow.loadError) {
		return (
			<div
				className="glass-card-sm p-6 text-sm text-red-600"
				data-testid="grounding-load-error"
			>
				Couldn&apos;t load your business context. Reload to try again.
			</div>
		);
	}

	const progress = flow.loading ? 0 : (tally.filled / tally.total) * 100;

	return (
		<div
			data-testid="grounding-flow"
			data-step={flow.stepIndex}
			data-progress={Math.round(progress)}
			data-answered={tally.filled}
			className="glass-card-sm relative flex min-h-[420px] flex-1 basis-0
				flex-col overflow-hidden"
		>
			{/* ONE card for the whole conversation. Answering appends a message; it does not
			    replace the screen. The old two-slot shuttle cross-faded the entire transcript on
			    every answer, which read as the panel reloading itself. Individual messages carry
			    their own entrance instead. */}
			<div className="relative min-h-0 flex-1 px-6 pt-3 pb-6">
				{flow.loading ? (
					<p className="text-sm text-(--text-muted)">Loading your business…</p>
				) : (
					renderStep(flow, flow.stepIndex, tally, footerAction, setWritingOwn)
				)}
			</div>

			{/* Announced to screen readers on every advance; lives in the shell so it survives
			    the card swap. */}
			<span aria-live="polite" role="status" className="sr-only">
				{flow.loading
					? ''
					: flow.stepIndex === REVIEW_STEP
						? 'Reviewing everything we know'
						: (fieldForStep(flow.stepIndex)?.question ??
							'And what number are you aiming for?')}
			</span>

			{flow.saveState === 'error' && (
				<div
					data-testid="grounding-save-error"
					className="flex flex-shrink-0 items-center gap-2 px-6 pb-3 text-xs
						text-red-600"
				>
					<span>We couldn&apos;t save that answer.</span>
					<button
						type="button"
						onClick={flow.retrySave}
						className="underline underline-offset-2"
					>
						Try again
					</button>
				</div>
			)}
		</div>
	);
}

function renderStep(
	flow: ReturnType<typeof useGroundingFlow>,
	index: number,
	tally: GroundingTally,
	footerAction?: ReactNode,
	onWritingChange?: (writing: boolean) => void,
	isExiting = false,
) {
	if (index === REVIEW_STEP) {
		return (
			<GroundingReviewStep
				tally={tally}
				answers={flow.answers}
				northStar={flow.northStar}
				northStarUnsaved={flow.northStarUnsaved}
				onConfirmNorthStar={flow.confirmNorthStar}
				onSaveAnswer={flow.commitFromReview}
				footerAction={footerAction}
			/>
		);
	}
	const field = fieldForStep(index);
	if (!field) return null;
	const isNorthStar = isNorthStarStep(index);
	return (
		<GroundingStepCard
			field={field}
			// The leaving card must not answer to `grounding-step` — while both are mounted a
			// query for it has to resolve to exactly one node, or every step assertion is
			// ambiguous for the length of the shuttle.
			isExiting={isExiting}
			index={index}
			value={
				isNorthStar
					? flow.northStar
						? formatNorthStar(flow.northStar)
						: ''
					: (flow.answers[field.id] ?? '')
			}
			conversation={flow.conversation}
			turn={flow.turn}
			turnLoading={flow.turnLoading}
			replyPending={flow.replyPending}
			onCancel={flow.cancelStep}
			onWritingChange={onWritingChange}
			onDraftChange={(value) => {
				if (!isNorthStar) flow.noteDraft(field.id, value);
			}}
			onSubmitAnswer={(value) =>
				isNorthStar
					? flow.submitNorthStar(value)
					: flow.submitAnswer(field.id, value)
			}
			onAdvance={flow.advanceAfterCommit}
			onCommit={(value) => {
				if (isNorthStar) {
					flow.commitNorthStar(value);
				} else {
					flow.commit(field.id, value);
				}
				flow.advanceAfterCommit();
			}}
		/>
	);
}
